/**
 * InkyPic API Service
 * Handles all API calls to Flask backend
 */

import axios from 'axios';

// API Base URL - defaults to localhost:5000 for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Check backend health status
 * @returns {Promise} API response with health status
 */
export const checkHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/health`);
    return response.data;
  } catch (error) {
    console.error('[InkyPic] Health check failed:', error);
    throw error;
  }
};

/**
 * Upload and colorize an image
 * @param {File} imageFile - The image file to colorize
 * @param {Function} onUploadProgress - Optional callback for upload progress
 * @returns {Promise} API response with colorized image data
 */
export const colorizeImage = async (imageFile, onUploadProgress = null) => {
  try {
    // Create FormData
    const formData = new FormData();
    formData.append('image', imageFile);

    // Make API call
    const response = await axios.post(
      `${API_BASE_URL}/api/colorize`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(percentCompleted);
        } : undefined,
      }
    );

    return response.data;
  } catch (error) {
    console.error('[InkyPic] Colorization failed:', error);

    // Extract error message from response if available
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || error.response.data.error || 'Colorization failed');
    }

    throw error;
  }
};

/**
 * Get the full URL for downloading a colorized image
 * @param {string} filename - The colorized image filename
 * @returns {string} Full download URL
 */
export const getDownloadUrl = (filename) => {
  return `${API_BASE_URL}/api/download/${filename}`;
};

/**
 * Download a colorized image
 * @param {string} filename - The colorized image filename
 * @returns {Promise} Blob data of the image
 */
export const downloadImage = async (filename) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/download/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('[InkyPic] Download failed:', error);
    throw error;
  }
};

/**
 * Validate image file before upload
 * @param {File} file - The file to validate
 * @returns {Object} Validation result with success flag and error message
 */
export const validateImageFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!file) {
    return { success: false, error: 'No file selected' };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      success: false,
      error: 'Invalid file type. Please upload a JPG or PNG image.'
    };
  }

  if (file.size > maxSize) {
    return {
      success: false,
      error: 'File too large. Maximum size is 10MB.'
    };
  }

  return { success: true };
};

export default {
  checkHealth,
  colorizeImage,
  getDownloadUrl,
  downloadImage,
  validateImageFile,
};
